import { reactive, ref, watch, type MaybeRef, unref } from 'vue';
import type { TransactionsStoreId } from '@/stores/exchangeTransactionsStore';
import type { BaseTransaction, OnboardingStatusNullable } from '@/types/transactions';
import type { ApiV1 } from '@teamworksdev/influencer/core';
import { useExchangePaymentsService } from '@/api/exchangePaymentsService';
import { handleError } from '@/utils/errorHandling';

interface UseExchangeTransactionPaymentState {
	isOpen: boolean;
	transactions: BaseTransaction[];
	costBreakdown: ApiV1.TransactionCostBreakdown | undefined;
	transactionsStoreId: TransactionsStoreId | undefined;
	onPaymentSent: (() => void) | undefined;
}

const DEFAULT_STATE: UseExchangeTransactionPaymentState = {
	isOpen: false,
	transactions: [],
	costBreakdown: undefined,
	transactionsStoreId: undefined,
	onPaymentSent: undefined,
};

const getDefaultState = () => ({ ...DEFAULT_STATE });

const state = reactive<UseExchangeTransactionPaymentState>(getDefaultState());
const exchangePaymentsService = useExchangePaymentsService();

const isOnboardingStatusLoading = ref(false);

function resetState() {
	Object.assign(state, getDefaultState());
}

const isRendered = ref<boolean>(false);

watch(() => state.isOpen, (isOpen) => {
	if (isOpen && !isRendered.value) isRendered.value = true;
	if (!isOpen) resetState();
});

interface UseExchangeTransactionPaymentDialogOptions {
	transactionsStoreId?: MaybeRef<TransactionsStoreId>;
	onPaymentSent?: () => void;
}

export function useExchangeTransactionPaymentDialog(options?: UseExchangeTransactionPaymentDialogOptions) {
	state.onPaymentSent = () => {
		close();
		options?.onPaymentSent?.();
	};

	const fetchOnboardingStatuses = async () => {
		const userUuids = [...new Set(
			state.transactions
				.map((t) => t.athlete?.uuid)
				.filter((uuid) => uuid !== undefined),
		)];

		if (!userUuids.length) return [];

		try {
			isOnboardingStatusLoading.value = true;

			const response = await exchangePaymentsService.getExchangePaymentsOnboardingStatus({ users: userUuids });

			return response.users;
		} catch (error) {
			handleError(error);
			return [];
		} finally {
			isOnboardingStatusLoading.value = false;
		}
	};

	function mapUserOnboardingStatuses(onboardingStatuses: ApiV1.PaymentsOnboardingStatus[]) {
		const statusMap = new Map<Uuid, OnboardingStatusNullable | null>();

		onboardingStatuses.forEach((user) => {
			const stripeStatus = user.onboarding_status.find((s) => s.service === 'stripe')?.status ?? null;
			const walletStatus = user.onboarding_status.find((s) => s.service === 'wallet')?.status ?? null;
			const isInternational = user.onboarding_status.some((s) => s.is_international);

			if (!stripeStatus && !walletStatus) {
				statusMap.set(user.uuid, null);
			} else {
				statusMap.set(user.uuid, {
					wallet: walletStatus,
					stripe: stripeStatus,
					isInternational,
					wallet_verification_status: null,
				});
			}
		});

		state.transactions = state.transactions.map((transaction) => {
			if (!transaction.athlete?.uuid || !statusMap.has(transaction.athlete.uuid)) return transaction;

			return {
				...transaction,
				onboardingStatus: statusMap.get(transaction.athlete.uuid)!,
			};
		});
	}

	async function open(transactions: MaybeArray<BaseTransaction>, costBreakdown?: ApiV1.TransactionCostBreakdown) {
		state.transactions = Array.isArray(transactions) ? transactions : [transactions];

		const onboardingStatuses = await fetchOnboardingStatuses();
		mapUserOnboardingStatuses(onboardingStatuses);

		state.costBreakdown = costBreakdown;
		state.transactionsStoreId = unref(options?.transactionsStoreId);

		state.isOpen = true;
	}

	function close() {
		state.isOpen = false;
	}

	return {
		state,
		isRendered,
		resetState,
		open,
		close,
		isOnboardingStatusLoading,
	};
}
